import * as yup from "yup";

import { useLazyQuery } from "@apollo/client";
import { useFormik } from "formik";
import { useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import clsx from "clsx";
import { useTranslation } from "next-i18next";
import { toast } from "react-toastify";
import { GET_WORKER_BY_UNIQUE_ID } from "../../graphql/workers.graphql";
import styles from "../Lines/Lines.module.scss";
import QRCodeReader from "../QRCodeReader";
import { useChangeCheck } from "./changeCheckHook";
import ReaderForm from "./ReaderForm";

import CheckinSubmitButton from "./CheckinSubmitButton";

import { useCheckLabor } from "./checkLaborHook";

const ReadWorkerQR = ({ openModal, setOpenModal, job, refreshData }) => {
  const onChangeCheck = useChangeCheck();

  const checkLabor = useCheckLabor();

  const { t } = useTranslation();

  const [qrData, setQRData] = useState();

  const [worker, setWorker] = useState();

  const [timelog, setTimelog] = useState();

  const [checkStatus, setCheckStatus] = useState("waiting");

  const [loadWorker] = useLazyQuery(GET_WORKER_BY_UNIQUE_ID, {
    fetchPolicy: "network-only", // Used for first execution
  });

  const fieldRequired = t("general.validation.required");

  const validationSchema = yup.object().shape({
    position: yup.object().when({
      is: () => checkStatus === "checkout",
      then: yup.object(),
      otherwise: yup.object().required(fieldRequired),
    }),
    reason: yup.object().when({
      is: () => checkStatus === "checkin",
      then: yup.object(),
      otherwise: yup.object().required(fieldRequired),
    }),
    description: yup.string().when("reason", (reason, schema) => {
      if (reason?.value === "OTHER") {
        return schema.required(fieldRequired);
      } else {
        return schema;
      }
    }),
  });

  const afterSubmit = () => {
    if (process.env.NODE_ENV !== 'production') {
      console.log("✅ afterSubmit CALLED (ReadWorkerQR.js)", { timestamp: new Date().toISOString() });
    }
    resetForm();
    refreshData();
    setCheckStatus("waiting");
    setWorker();
    setTimelog();
    setQRData("");
  };

  const formik = useFormik({
    initialValues: {
      position: "",
      reason: "",
      description: "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) =>
      checkLabor(
        afterSubmit,
        worker,
        timelog,
        values,
        setSubmitting,
        checkStatus,
        job
      ),
  });

  const { setFieldValue, resetForm, isSubmitting, setSubmitting } = formik;

  const disabledCheck = isSubmitting || checkStatus === "waiting";

  const readWorker = async (uniqueId) => {
    const response = await loadWorker({
      variables: { unique_access_id: uniqueId },
    });
    try {
      let worker = response?.data?.worker[0] || {};
      worker = { ...worker, unique_access_id: uniqueId };
      if (!!worker.id) {
        setWorker(worker);
        const status = await onChangeCheck(
          job,
          worker,
          setFieldValue,
          setTimelog
        );
        setCheckStatus(status);
      } else {
        toast.error(t("views.checkin.form.noWorkerExistOrActive"));
      }
    } catch (e) {
      console.log(e);
      toast.error(t("general.somethingWrong"));
    }
  };

  return (
    <QRCodeReader
      open={openModal === "READ"}
      closeOnRead={true}
      hide={() => {
        setWorker(undefined);
        setQRData(undefined);
        setOpenModal("");
      }}
      onRead={(id) => {
        setQRData(id);
        readWorker(id);
      }}
      onSave={(id) => console.log(id)}
      data={qrData}
      title={t("views.dashboard.leader.scan")}
      showFooter={true}
      info={
        qrData ? (
          <div className="d-flex flex-row justify-content-between">
            <div className="d-flex flex-column">
              <h1 className="mb-0 fw-bold">{`${worker?.first_name || ""} ${
                worker?.last_name || ""
              }`}</h1>
              <p className="mb-0">{qrData}</p>
            </div>
            <OverlayTrigger
              key="reset"
              overlay={
                <Tooltip id={`tooltip-reset`}>{t("views.table.reset")}</Tooltip>
              }
            >
              <i
                role="button"
                className={clsx("bi-trash", styles.action)}
                onClick={() => {
                  setWorker(undefined);
                  setQRData(undefined);
                }}
              />
            </OverlayTrigger>
          </div>
        ) : null
      }
      form={<ReaderForm checkStatus={checkStatus} job={job} formik={formik} />}
      withManualInput
      submitButton={
        <CheckinSubmitButton
          checkStatus={checkStatus}
          disabledCheck={disabledCheck}
        />
      }
    />
  );
};

export default ReadWorkerQR;
